import { SubmissionParticipantWithValidDecennialCertificate } from "@dune/types";
import { PAGES } from "@dune/types";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
	Button,
	H2,
	Separator,
} from "@dune/ui";
import { formatCurrency } from "@dune/utils";
import { useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
	useDeleteParticipantFromConstructionSiteSubmission,
	useLinkExternalDecennialCertificates,
	useSubmitConstructionSiteSubmission,
} from "../../../api/mutations/constructionSiteSubmission.mutations";
import {
	useGetConstructionSiteSubmissionOperation,
	useGetConstructionSiteSubmissionParticipants,
} from "../../../api/queries/constructionSiteSubmission.queries";
import { constructionSiteSubmissionQueryKeys } from "../../../api/query-keys/constructionSiteSubmission.query-keys";
import { BuildersTable } from "../../../component/submission-participants/BuildersTable";
import { DesignersTable } from "../../../component/submission-participants/DesignersTable";
import { MaterialsTable } from "../../../component/submission-participants/MaterialsTable";
import { useShouldDisplayHTAmount } from "../../../hooks/useParticipantToSubmissionForm";
import { useRequiredParams } from "../../../hooks/useRequiredParams";
import ConstructionCostValidationInline from "../components/ConstructionCostValidationInline";

interface SubmissionParticipantsProps {
	handlePreviousStep: () => void;
	isUpdateDisabled?: boolean;
}

export const SubmissionParticipants = ({
	handlePreviousStep,
	isUpdateDisabled = false,
}: SubmissionParticipantsProps) => {
	const navigate = useNavigate();
	const { constructionSiteId } = useRequiredParams<{
		constructionSiteId: string;
	}>();

	const [selectedParticipantId, setSelectedParticipantId] = useState<
		number | null
	>(null);

	const [validationError, setValidationError] = useState<{
		totalDeclaredAmount: number;
		constructionCost: number;
		errorCode?: string;
	} | null>(null);

	const {
		data: submissionWithParticipants,
		isLoading,
		error,
	} = useGetConstructionSiteSubmissionParticipants(Number(constructionSiteId));
	const { data: submissionWithOperationDetails } =
		useGetConstructionSiteSubmissionOperation(Number(constructionSiteId));

	const { shouldDisplayHT } = useShouldDisplayHTAmount(
		Number(constructionSiteId),
	);

	const { designers, builders, materials } = useMemo(() => {
		const result: {
			designers: SubmissionParticipantWithValidDecennialCertificate[];
			builders: SubmissionParticipantWithValidDecennialCertificate[];
			materials: SubmissionParticipantWithValidDecennialCertificate[];
		} = {
			designers: [],
			builders: [],
			materials: [],
		};

		if (!submissionWithParticipants) return result;
		for (const submissionParticipant of submissionWithParticipants.participantToConstructionSiteSubmissions) {
			if (submissionParticipant.type === "DESIGNER") {
				result.designers.push(submissionParticipant);
			} else if (submissionParticipant.type === "BUILDER") {
				result.builders.push(submissionParticipant);
			} else if (submissionParticipant.type === "MATERIAL") {
				result.materials.push(submissionParticipant);
			}
		}

		return result;
	}, [submissionWithParticipants]);

	const totalAmountDeclared = useMemo(() => {
		const reduceFunc = (
			acc: number,
			participant: SubmissionParticipantWithValidDecennialCertificate,
		) => {
			const amount = shouldDisplayHT
				? participant.totalFeesHT
				: participant.totalFeesTTC;
			return acc + (amount || 0);
		};

		const designersTotal = designers.reduce(reduceFunc, 0);
		const buildersTotal = builders.reduce(reduceFunc, 0);
		const materialsTotal = materials.reduce(reduceFunc, 0);

		return designersTotal + buildersTotal + materialsTotal;
	}, [designers, builders, materials, shouldDisplayHT]);

	const constructionCost = useMemo(() => {
		const totalAmount = shouldDisplayHT
			? submissionWithOperationDetails?.constructionSite
					?.constructionSiteDetails?.totalAmountHT
			: submissionWithOperationDetails?.constructionSite
					?.constructionSiteDetails?.totalAmountTTC;
		return totalAmount || 0;
	}, [submissionWithOperationDetails, shouldDisplayHT]);

	const { mutate: deleteParticipant } =
		useDeleteParticipantFromConstructionSiteSubmission(
			Number(constructionSiteId),
		);

	const handleParticipantSelect = (
		participantId: number,
		isSelected: boolean,
	) => {
		if (isSelected) {
			setSelectedParticipantId(participantId);
		} else {
			setSelectedParticipantId(null);
		}
	};

	const queryClient = useQueryClient();

	const handleDeleteParticipant = () => {
		if (selectedParticipantId) {
			deleteParticipant(selectedParticipantId, {
				onSuccess: () => {
					setSelectedParticipantId(null);
					queryClient.invalidateQueries({
						queryKey: constructionSiteSubmissionQueryKeys.participants(
							Number(constructionSiteId),
						),
					});
				},
			});
		}
	};

	const handleEditParticipant = () => {
		if (selectedParticipantId && submissionWithParticipants) {
			const submissionParticipant =
				submissionWithParticipants.participantToConstructionSiteSubmissions.find(
					(p) => p.participant.id === selectedParticipantId,
				);
			if (submissionParticipant) {
				if (submissionParticipant.type === "DESIGNER") {
					navigate(
						PAGES.PROJECTS.submission.participants.editDesignerWithParams(
							constructionSiteId,
							selectedParticipantId.toString(),
						),
					);
				} else if (submissionParticipant.type === "BUILDER") {
					navigate(
						PAGES.PROJECTS.submission.participants.editBuilderWithParams(
							constructionSiteId,
							selectedParticipantId.toString(),
						),
					);
				} else if (submissionParticipant.type === "MATERIAL") {
					navigate(
						PAGES.PROJECTS.submission.participants.editMaterialWithParams(
							constructionSiteId,
							selectedParticipantId.toString(),
						),
					);
				}
			}
		}
	};

	const { mutate: submitSubmission, isPending: isSubmitting } =
		useSubmitConstructionSiteSubmission();

	const { mutate: linkExternalDecennialCertificates } =
		useLinkExternalDecennialCertificates(Number(constructionSiteId));
	if (isLoading) return <div>Chargement des intervenants...</div>;
	if (error) return <div>Erreur lors du chargement des intervenants.</div>;

	const handleAddDesigner = () => {
		navigate(
			PAGES.PROJECTS.submission.participants.addDesignerWithParams(
				constructionSiteId,
			),
		);
	};

	const handleAddBuilder = () => {
		navigate(
			PAGES.PROJECTS.submission.participants.addBuilderWithParams(
				constructionSiteId,
			),
		);
	};

	const handleAddMaterial = () => {
		navigate(
			PAGES.PROJECTS.submission.participants.addMaterialWithParams(
				constructionSiteId,
			),
		);
	};

	const handleSaveAndClose = () => {
		// External decennial certificates are now automatically linked when participants are added
		linkExternalDecennialCertificates(undefined, {
			onSuccess: () => {
				navigate(PAGES.PROJECTS.show.withParams(constructionSiteId));
			},
		});
	};

	const handleSubmit = () => {
		// Link external certificates as safety net before submission (in case new certificates became available)
		linkExternalDecennialCertificates(undefined, {
			onSuccess: () => {
				submitSubmission(
					{ constructionSiteId: Number(constructionSiteId) },
					{
						onSuccess: () => {
							queryClient.invalidateQueries({
								queryKey: constructionSiteSubmissionQueryKeys.participants(
									Number(constructionSiteId),
								),
							});
							navigate(PAGES.PROJECTS.show.withParams(constructionSiteId));
						},
						onError: (error: unknown) => {
							const errorData = (
								error as {
									response?: {
										data?: {
											code?: string;
											totalDeclaredAmount?: number;
											constructionCost?: number;
											requiresPricingCheck?: boolean;
										};
									};
								}
							)?.response?.data;

							if (
								errorData?.code === "CONSTRUCTION_COST_VALIDATION_REQUIRED" ||
								errorData?.code === "CONSTRUCTION_COST_EXCEEDED" ||
								errorData?.code === "DECLARED_AMOUNT_TOO_LOW"
							) {
								setValidationError({
									totalDeclaredAmount: errorData.totalDeclaredAmount || 0,
									constructionCost: errorData.constructionCost || 0,
									errorCode: errorData.code,
								});
							}
						},
					},
				);
			},
		});
	};

	const handleModifyCost = () => {
		navigate(PAGES.PROJECTS.edit.withParams(constructionSiteId));
	};

	const handleKeepCost = () => {
		submitSubmission(
			{
				constructionSiteId: Number(constructionSiteId),
				options: { bypassLowAmountCheck: true },
			},
			{
				onSuccess: () => {
					setValidationError(null);
					queryClient.invalidateQueries({
						queryKey: constructionSiteSubmissionQueryKeys.participants(
							Number(constructionSiteId),
						),
					});
					navigate(PAGES.PROJECTS.show.withParams(constructionSiteId));
				},
				onError: (error: unknown) => {
					const errorMessage =
						(error as { message?: string })?.message ||
						"Une erreur est survenue";
					toast.error("Erreur lors de la validation", {
						description: errorMessage,
					});
				},
			},
		);
	};

	const handleValidationCancel = () => {
		setValidationError(null);
	};

	const isSubmitted =
		submissionWithParticipants?.constructionSite.status === "TO_ANALYZE";

	// Mode switch: soit on affiche les participants, soit la validation
	if (validationError) {
		return (
			<div>
				<ConstructionCostValidationInline
					onModifyCost={handleModifyCost}
					onCancel={handleValidationCancel}
					onKeepCost={handleKeepCost}
					totalDeclaredAmount={validationError.totalDeclaredAmount}
					constructionCost={validationError.constructionCost}
					errorCode={validationError.errorCode}
				/>
			</div>
		);
	}

	return (
		<div>
			<div className="flex justify-between items-start mt-4 mb-8">
				<H2>Intervenants</H2>
				{!isUpdateDisabled && (
					<div className="flex gap-2">
						<Button
							variant="outline"
							disabled={!selectedParticipantId}
							onClick={handleEditParticipant}
						>
							Modifier
						</Button>
						<AlertDialog>
							<AlertDialogTrigger asChild>
								<Button variant="destructive" disabled={!selectedParticipantId}>
									Supprimer
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
									<AlertDialogDescription>
										Êtes-vous sûr de vouloir supprimer ce participant de la
										soumission ? Cette action est irréversible.
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>Annuler</AlertDialogCancel>
									<AlertDialogAction onClick={handleDeleteParticipant}>
										Supprimer
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</div>
				)}
			</div>

			<div className="space-y-6">
				<div>
					<DesignersTable
						submissionParticipants={designers}
						constructionSiteId={Number(constructionSiteId)}
						selectedParticipantId={selectedParticipantId}
						onParticipantSelect={handleParticipantSelect}
						readonly={isUpdateDisabled}
					/>
					{!isUpdateDisabled && (
						<Button onClick={handleAddDesigner} className="h-12">
							<PlusIcon /> Ajouter un concepteur
						</Button>
					)}
				</div>

				<Separator />

				<div>
					<BuildersTable
						submissionParticipants={builders}
						constructionSiteId={Number(constructionSiteId)}
						selectedParticipantId={selectedParticipantId}
						onParticipantSelect={handleParticipantSelect}
						readonly={isUpdateDisabled}
					/>
					{!isUpdateDisabled && (
						<Button onClick={handleAddBuilder} className="h-12">
							<PlusIcon /> Ajouter un réalisateur
						</Button>
					)}
				</div>

				<Separator />

				<div>
					<MaterialsTable
						submissionParticipants={materials}
						constructionSiteId={Number(constructionSiteId)}
						selectedParticipantId={selectedParticipantId}
						onParticipantSelect={handleParticipantSelect}
						readonly={isUpdateDisabled}
					/>
					{!isUpdateDisabled && (
						<Button onClick={handleAddMaterial} className="h-12">
							<PlusIcon /> Ajouter un matériau
						</Button>
					)}
				</div>
			</div>

			<Separator className="mt-8" />

			<div className="mt-8">
				<div className="text-right text-base font-semibold text-text-main-blue mb-4 flex gap-3 justify-end">
					<div>TOTAL DES MONTANTS DÉCLARÉS</div>
					<div>
						{formatCurrency(totalAmountDeclared)}{" "}
						{shouldDisplayHT ? "HT" : "TTC"}
					</div>
				</div>
				<div className="text-right text-sm text-text-secondary-blue flex ml-auto gap-3 justify-end">
					<div>Coût de construction prévisionnel déclaré</div>
					<div>
						{formatCurrency(constructionCost)} {shouldDisplayHT ? "HT" : "TTC"}
					</div>
				</div>
			</div>

			<div className="flex justify-between mt-8">
				<Button variant="outline" onClick={handlePreviousStep}>
					Précédent
				</Button>
				<div className="flex gap-2">
					{!isUpdateDisabled && (
						<Button variant="outline" onClick={handleSaveAndClose}>
							Enregistrer et fermer
						</Button>
					)}
					{!isSubmitted && (
						<Button
							disabled={isSubmitting}
							isLoading={isSubmitting}
							onClick={() => {
								isUpdateDisabled
									? navigate(PAGES.PROJECTS.show.withParams(constructionSiteId))
									: handleSubmit();
							}}
							variant="default"
						>
							{!isUpdateDisabled
								? "Valider la soumission du chantier"
								: "Retour aux détails du chantier"}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};
